import React, { useState } from 'react';
import { db } from '../config/firebase';
import { collection, addDoc } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import SEO from './SEO';

import donationHeroImage from '../../src/assets/images/hsi_hero2.png';

const Donate: React.FC = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pledgeAmount, setPledgeAmount] = useState('');
  const [donationType, setDonationType] = useState('One-Time');
  const [reminder, setReminder] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !phone) {
      toast.error('Please fill in your name and phone number.');
      return;
    }

    const toastId = toast.loading('Submitting your pledge...');

    try {
      await addDoc(collection(db, 'pledges'), {
        fullName,
        email,
        phone,
        pledgeAmount,
        donationType,
        reminder,
        pledgedAt: new Date(),
      });

      toast.success('Thank you for your pledge! We will be in touch.', { id: toastId });

      // Reset form
      setFullName('');
      setEmail('');
      setPhone('');
      setPledgeAmount('');
      setDonationType('One-Time');
      setReminder(false);
    } catch (error) {
      console.error('Error adding document: ', error);
      toast.error('Something went wrong. Please try again.', { id: toastId });
    }
  };
  return (
    <>
      <SEO 
        title="Donate - Support Healthier Smile Initiatives"
        description="Support Healthier Smile Initiatives by making a donation or pledging your support. Your contribution helps us provide  care to communities in Sierra Leone."
        name="Healthier Smile Initiatives"
        type="website"
      />
      <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Toaster position="top-center" reverseOrder={false} />
        <div className="max-w-4xl mx-auto">

        {/* Header Section */}
        <section className="text-center mb-12">
          <img src={donationHeroImage} alt="Hands giving and receiving a toothbrush" className="w-full h-64 object-cover rounded-lg shadow-lg mb-6"/>
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">Your Support Creates a Healthier Smile</h1>
          <p className="mt-4 text-xl text-gray-600">Join us in building a future where every person in Sierra Leone has access to the medical and dental care they deserve. Your contribution, big or small, helps prevent suffering and empowers communities.</p>
        </section>

        {/* How You Can Help Section */}
        <section className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-2">How You Can Help</h2>
            <p className="text-center text-gray-600 mb-8">We are working on integrating a direct online payment system to make donating easier. In the meantime, you can support our vital work through the following methods. Every donation, whether general or for a specific activity, makes a significant difference.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-2xl font-semibold mb-4">Direct Donation & Inquiries</h3>
                    <p className="mb-4">For all personal or organizational donations, to discuss our specific needs, or to volunteer your time, please contact us directly. We welcome your partnership.</p>
                    <p><strong>Organization:</strong> Healthier Smile Initiatives (HSI)</p>
                    <p><strong>Address:</strong> #151 Dambala Road, Kulanda Town-Bo</p>
                    <p><strong>Email:</strong> healthiersmileinitiative@gmail.com</p>
                    <p><strong>Telephone:</strong> +23278036464 / +23278656579</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-2xl font-semibold mb-4">Direct Bank Transfer</h3>
                    <p className="mb-4">You can make a direct deposit to our official bank account. Please consider adding a note for the transaction, such as 'General Donation' or specifying an activity.</p>
                    <p><strong>Account Name:</strong> Healthier Smile Initiatives</p>
                    <p><strong>Bank Name:</strong> ROKEL COMMERCIAL BANK SIERRA LEONE LIMITED</p>
                    <p><strong>Bank Address:</strong> 25/27 SIAKA STEVENS STREET FREETOWN</p>
                    <p><strong>Account Number:</strong> 0033095611101</p>
                    <p><strong>BBAN Number:</strong> 002003309561110101</p>
                    <p><strong>SWIFT/BIC Code:</strong> RCBKSLFR</p>
                    
                </div>
            </div>
        </section>

        {/* Support a Specific Activity Section */}
        <section className="mb-12 bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-2xl font-semibold mb-4 text-center">Support a Specific Activity</h3>
            <p className="text-center mb-6">You can choose to direct your support towards a specific part of our mission, such as:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mx-auto max-w-md">
                <li>Training Medical and Dental CHOs and Nurses</li>
                <li>Funding a Mobile Clinic to a remote area</li>
                <li>Providing supplies for Community Medical and Dental Screenings</li>
                <li>Creating Oral Health Educational Materials for schools</li>
                <li>Acquiring essential dental equipment and materials</li>
            </ul>
        </section>

        {/* Pledge Your Support Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-center">Commit to a Healthier Future</h2>
          <p className="text-center text-gray-600 mt-2 mb-6">Can't donate today? Make a pledge to show your support. We'll send you a one-time, friendly reminder to fulfill your commitment to building healthier smiles across Sierra Leone.</p>
                    <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white p-8 rounded-lg shadow-md">
            <div className="mb-4">
              <label htmlFor="fullName" className="block text-gray-700 font-semibold mb-2">Full Name</label>
                            <input type="text" id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="mb-4">
              <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">Email Address (Optional)</label>
                            <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="mb-4">
              <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">Phone Number</label>
              <input type="tel" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="mb-4">
              <label htmlFor="pledgeAmount" className="block text-gray-700 font-semibold mb-2">Pledge Amount (SLL/USD - Optional)</label>
              <input type="text" id="pledgeAmount" value={pledgeAmount} onChange={(e) => setPledgeAmount(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="mb-4">
                <p className="block text-gray-700 font-semibold mb-2">I plan to donate:</p>
                <div className="flex items-center">
                                        <input type="radio" id="one-time" name="donation-type" value="One-Time" checked={donationType === 'One-Time'} onChange={(e) => setDonationType(e.target.value)} className="mr-2"/>
                    <label htmlFor="one-time">One-Time</label>
                </div>
                <div className="flex items-center mt-2">
                                        <input type="radio" id="monthly" name="donation-type" value="Monthly" checked={donationType === 'Monthly'} onChange={(e) => setDonationType(e.target.value)} className="mr-2"/>
                    <label htmlFor="monthly">Monthly</label>
                </div>
            </div>
            <div className="mb-6">
                            <input type="checkbox" id="reminder" checked={reminder} onChange={(e) => setReminder(e.target.checked)} className="mr-2" />
              <label htmlFor="reminder">Yes, please send me a reminder about my pledge in 30 days.</label>
            </div>
            <button type="submit" className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors">PLEDGE MY SUPPORT</button>
          </form>
        </section>

        {/* Your Impact Section */}
        <section>
          <h2 className="text-3xl font-bold text-center">See How Your Donation Changes Lives</h2>
          <p className="text-center text-gray-600 mt-2 mb-8">Financial barriers and a lack of access to care are major challenges for the people we serve. Your support directly overcomes these hurdles.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-2">Reach Isolated Communities</h3>
              <p>A contribution can provide transportation for our medical and dental teams to reach isolated communities.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-2">Empower Local Health Workers</h3>
              <p>Your donation helps us train, equip, and empower local Community Health Professionals (CHOs) and Nurses with vital medical and dental skills.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-semibold text-lg mb-2">Support Vulnerable Groups</h3>
              <p>You can help us supply oral hygiene kits to pregnant women, lactating mothers, and schoolchildren—our most vulnerable groups.</p>
            </div>
             <div className="bg-white p-6 rounded-lg shadow-md md:col-span-2 lg:col-span-3">
              <h3 className="font-semibold text-lg mb-2">Provide Essential Treatments</h3>
              <p>With your support, we can purchase the necessary dental supplies and equipment to perform essential treatments and prevent life-threatening complications.</p>
            </div>
          </div>
        </section>

      </div>
    </div>
    </>
  );
};

export default Donate;